// Footer Component
// ================
// Feature #287: Refined with subtle design-line accent

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AIDisclaimer from '../ui/AIDisclaimer';
import { LyraLogo } from '../ui';
import DesignLine from '../ui/DesignLine';

const Footer: React.FC = () => {
  const { t } = useTranslation('landing');
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-dark dark:bg-gray-950 text-white relative">
      {/* Feature #287: Design-line accent at top of footer */}
      <DesignLine variant="horizontal" className="absolute top-0 left-0 right-0" height={2} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 pt-14 md:pt-18">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand column - Feature #155: SVG logo */}
          <div className="md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <LyraLogo size="md" showText={true} />
            </Link>
            <p className="text-gray-400 text-sm mb-4">
              {t('footer.tagline')}
            </p>
            <p className="text-gray-500 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
          </div>

          {/* Product column */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.product')}</h3>
            <ul className="space-y-3">
              <li>
                <a href="#features" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.features')}
                </a>
              </li>
              <li>
                <a href="#how-it-works" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.howItWorks')}
                </a>
              </li>
              <li>
                <span className="text-gray-500 text-sm">{t('footer.pricingComingSoon')}</span>
              </li>
            </ul>
          </div>

          {/* Support column */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.support')}</h3>
            <ul className="space-y-3">
              <li>
                <span className="text-gray-500 text-sm">{t('footer.helpComingSoon')}</span>
              </li>
              <li>
                <a href="mailto:support@lyra.dk" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.contact')}
                </a>
              </li>
              <li>
                <span className="text-gray-500 text-sm">{t('footer.faqComingSoon')}</span>
              </li>
            </ul>
          </div>

          {/* Legal column - Feature #143 */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.cookies')}
                </Link>
              </li>
              <li>
                <Link to="/gdpr" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  {t('footer.gdpr')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            {/* Kierkegaard quote - Feature #201 */}
            <p className="text-gray-400 text-sm italic max-w-2xl text-center md:text-left">
              &ldquo;{t('footer.kierkegaardQuote')}&rdquo;
              <span className="text-gray-500 not-italic ml-1">&mdash; {t('footer.kierkegaardAttribution')}</span>
            </p>

            {/* Social links placeholder */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-500 text-sm">{t('footer.followUs')}</span>
              <div className="flex space-x-3">
                <a
                  href="#"
                  className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                  aria-label="Twitter"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* AI Disclaimer */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <AIDisclaimer variant="compact" />
          </div>

          {/* ContentDesign crediting - Feature #142, #202, #203 */}
          <div className="mt-6 pt-6 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">
              {t('footer.byLine')}{' '}
              <a
                href="https://contentdesign.dk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 transition-colors"
              >
                Content Design
              </a>
              , Denmark &ndash; {t('footer.contentDesignTagline')}
            </p>
            <p className="text-gray-500 text-xs mt-2">
              {t('footer.coCreated')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
